<template>
  <!-- A click opens the report behind the tile, already filtered, so the total it arrives at is
       the figure shown here (item 4, decision 2a). Only a tile that has somewhere to go gets the
       pointer. -->
  <div
    :class="'overview-box ' + styleClass + (drillable ? ' dashboard-drillable' : '')"
    @click="openReport"
  >
    <span class="overview-title">{{ widgetData.widgetLabel }}</span>
    <!-- <i class="overview-arrow pi pi-chevron-circle-up"></i> -->
    <i v-if="widgetData.widgetIcon" :class="'overview-icon pi pi-' + widgetData.widgetIcon.toLowerCase()"></i>
    <div class="overview-numbers">
      <span>{{ getToShow(widgetData.value, widgetData.valueType) }}</span>
    </div>
    <!-- The second value (item 4, task 4.3; W4 contract §5-§6). The backend decides whether this
         widget can compare at all: when it cannot, nothing is rendered here - no empty row, no
         dash. That is a different statement from "Dates do not apply" below, and both can be true
         of the same tile, so they stay separate elements. -->
    <div
      v-if="comparison"
      class="overview-subinfo dashboard-compare"
      :class="'dashboard-compare-' + comparison.direction"
    >
      <template v-if="comparison.hasPrior">
        <span class="dashboard-compare-delta">
          <!-- The direction must be readable without the colour: the arrow's shape and the word
               carry it, and the colour only reinforces. Up is not automatically good - these tiles
               happen to measure value, but the component is generic - so the pair is neutral. -->
          <i
            v-if="comparison.arrow"
            :class="'dashboard-compare-arrow pi ' + comparison.arrow"
            aria-hidden="true"
          ></i>
          <span class="dashboard-compare-pct">{{ comparison.percent }}</span>
          <span class="dashboard-compare-word">{{ comparison.word }}</span>
        </span>
        <!-- The prior figure itself, formatted by the same formatter as the figure above, so a
             percentage is checkable against the number it came from (contract §5). -->
        <span v-if="comparison.prior !== null" class="dashboard-compare-prior">
          vs {{ comparison.prior }}
        </span>
      </template>
      <!-- Never "-100%", never "NaN" (contract §4). -->
      <span v-else class="dashboard-compare-none">No prior data</span>
      <!-- The sentence is the backend's wording, not rebuilt here (contract §5). -->
      <span v-if="comparison.sentence" class="dashboard-compare-msg">{{ comparison.sentence }}</span>
    </div>
    <!-- Tiles 1 and 2 have no date field: the bar's date cannot narrow them (decision 5). -->
    <div v-if="datesSet && widgetData.datesApply === false" class="overview-subinfo dashboard-dates-note">
      Dates do not apply
    </div>
  </div>
</template>

<script>
import { drillTarget, hasDrill } from './dashboardDrill'
import { formatWidgetValue } from './widgetValue'

// The direction vocabulary the backend sends (contract §4). The arrow shape and the word are what
// make the direction readable; neither says whether the move is good or bad, because that depends
// on what the widget measures and the builder (W5) can make widgets that measure anything.
const DIRECTIONS = {
  up: { arrow: 'pi-arrow-up', word: 'up' },
  down: { arrow: 'pi-arrow-down', word: 'down' },
  same: { arrow: null, word: 'no change' }
}

const NO_PRIOR = 'No prior data'

export default {
  props: {
    widget: {
      type: Object,
      required: false
    },
    widgetData: {
      type: Object,
      required: false,
      default: () => ({})
    },
    styleClass: {
      type: String,
      default: ''
    },
    // True when the dashboard filter bar has a date set.
    datesSet: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // The reports this user actually has; a tile pointing at one they cannot open is not a link.
    reports() {
      return (this.$store && this.$store.getters['reports/getAllReports']) || []
    },
    drillable() {
      return hasDrill(this.widgetData, this.reports)
    },
    // Everything the comparison row needs, or null when this widget cannot compare at all - a
    // snapshot of current status has no prior window to compare with (contract §2), and a tile
    // that cannot compare renders none of this markup.
    comparison() {
      const data = this.widgetData || {}
      if (!data.compareAvailable) return null

      const delta = Number(data.compareDelta)
      // No prior figure, or a prior of zero, means no percentage exists: the backend sends a null
      // delta and the tile says so rather than inventing -100% or NaN (contract §4).
      const hasPrior =
        data.compareDelta !== null && data.compareDelta !== undefined && Number.isFinite(delta)

      const named = DIRECTIONS[data.compareDirection]
        ? data.compareDirection
        : delta > 0
          ? 'up'
          : delta < 0
            ? 'down'
            : 'same'
      const direction = hasPrior ? named : 'none'
      const shown = hasPrior ? DIRECTIONS[named] : { arrow: null, word: '' }

      const msg = typeof data.compareMsg === 'string' ? data.compareMsg.trim() : ''
      const prior =
        data.compareValue === null || data.compareValue === undefined
          ? null
          : this.getToShow(data.compareValue, data.valueType)

      return {
        hasPrior,
        direction,
        arrow: shown.arrow,
        word: shown.word,
        // The sign is carried by the arrow and the word, so the figure itself is unsigned.
        percent: hasPrior ? `${Math.abs(delta).toFixed(1)}%` : '',
        prior: hasPrior ? prior : null,
        // The backend says "No prior data" too; say it once, in the element that owns it.
        sentence: msg && !(!hasPrior && msg === NO_PRIOR) ? msg : ''
      }
    }
  },
  methods: {
    openReport() {
      const target = drillTarget(this.widgetData, this.reports)
      // A push, never a replace: a replace would take the dashboard's history entry with it and
      // Back would skip past it (item 2, D15).
      if (target) this.$router.push(target)
    },
    // W7 tail: one formatter for the tile and the list widget, so the tile can never again be
    // the copy that is missing a valueType.
    getToShow: formatWidgetValue
  }
}
</script>

<style>
/* The comparison row (item 4, task 4.3). Nothing here is rendered at all for a widget that
   cannot compare, so there is no empty row and no margin left behind. */
.dashboard-compare {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
}

.dashboard-compare-delta {
  font-weight: 600;
  white-space: nowrap;
}

.dashboard-compare-arrow {
  font-size: 0.75rem;
  margin-right: 0.15rem;
  vertical-align: baseline;
}

.dashboard-compare-word {
  margin-left: 0.2rem;
  font-weight: 400;
}

.dashboard-compare-prior,
.dashboard-compare-msg,
.dashboard-compare-none {
  white-space: nowrap;
}

.dashboard-compare-msg {
  flex-basis: 100%;
  white-space: normal;
}

/* A neutral pair - blue and amber, distinguishable without colour vision and carrying no verdict.
   Green/red would say "up is good", and up is not automatically good: the same component will show
   overdue activities or cancelled orders once the builder (W5) can make those widgets. The arrow
   and the word carry the direction on their own; this only reinforces them. */
.dashboard-compare-up .dashboard-compare-delta {
  color: #0b6bcb;
}

.dashboard-compare-down .dashboard-compare-delta {
  color: #9a5b00;
}

.dashboard-compare-none {
  font-style: italic;
  opacity: 0.85;
}
</style>